import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useSearchParams } from 'react-router-dom';
import { marked } from 'marked';
import { Search } from 'lucide-react';
import './ManualPage.css';

// Gives every heading an id (e.g. "How it works" -> "how-it-works") so a
// link can target one directly: /global-manual?article=slug#how-it-works.
// usedHeadingSlugs is cleared before each article parses (see the markdown
// useEffect below) so it only dedupes repeats within one article, not
// across every article ever loaded in the session.
const usedHeadingSlugs = new Map();
function slugifyHeading(text) {
  return text
    .replace(/[*_`]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
marked.use({
  renderer: {
    heading(token) {
      const html = this.parser.parseInline(token.tokens);
      let slug = slugifyHeading(token.text);
      const count = usedHeadingSlugs.get(slug) || 0;
      usedHeadingSlugs.set(slug, count + 1);
      if (count > 0) slug = `${slug}-${count}`;
      return `<h${token.depth} id="${slug}">${html}</h${token.depth}>\n`;
    },
  },
});

// The article list is discovered by the backend from public/manual/'s own
// folder structure (see manualArticleController.js) — adding a page is
// "drop a .md file in the right folder," nothing to edit here.
//
// Deep link shape shared with FieldHelp.js: /global-manual?article=<slug> —
// falls back to the first article when the slug is missing or unknown, so
// a stale/typo'd link never lands on a blank page. `initialSlug` (from
// HelpSheet, which knows the current screen's chosen article via Screen
// Settings) takes priority over the URL — the host page's own URL isn't
// meant to change just because its help sheet opened to a specific article.
function ManualPage({ initialSlug }) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSlug = initialSlug || searchParams.get('article');
  const [allArticles, setAllArticles] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(requestedSlug || null);
  // Set instead of silently substituting some other article when a
  // requested slug isn't in the (possibly role-filtered) list — e.g. a
  // Technical Guide link followed while that Group is hidden. Distinct from
  // "nothing selected yet" so the empty state can say why, not just sit
  // blank the way a genuinely article-less first load would.
  const [notFoundSlug, setNotFoundSlug] = useState(null);
  const [query, setQuery] = useState('');
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get('/api/manual-articles')
      .then((res) => res.data)
      .then((data) => {
        const articles = data?.data || [];
        setAllArticles(articles);
        if (requestedSlug && !articles.some((a) => a.slug === requestedSlug)) {
          setNotFoundSlug(requestedSlug);
          setSelectedSlug(null);
        } else {
          setSelectedSlug(requestedSlug || articles[0]?.slug || null);
        }
      })
      .catch(() => setAllArticles([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A fresh navigation to a new ?article= (e.g. clicking another field's
  // "Read full article" link while the Manual tab is already open) should
  // still jump straight to that article.
  useEffect(() => {
    if (!requestedSlug || requestedSlug === selectedSlug) return;
    if (allArticles.some((a) => a.slug === requestedSlug)) {
      setNotFoundSlug(null);
      setSelectedSlug(requestedSlug);
    } else if (allArticles.length > 0) {
      // Articles have loaded and the requested slug still isn't among
      // them — it's genuinely unavailable, not just "not fetched yet".
      setNotFoundSlug(requestedSlug);
      setSelectedSlug(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedSlug, allArticles]);

  const selectArticle = (slug) => {
    setNotFoundSlug(null);
    setSelectedSlug(slug);
    setSearchParams(slug ? { article: slug } : {});
  };

  const selected = allArticles.find((article) => article.slug === selectedSlug) || null;

  useEffect(() => {
    if (!selected) {
      setHtml('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`/manual/${selected.file}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Could not load ${selected.file}`);
        return res.text();
      })
      .then((markdown) => {
        if (!cancelled) {
          usedHeadingSlugs.clear();
          setHtml(marked.parse(markdown));
        }
      })
      .catch(() => {
        if (!cancelled) setHtml('<p>This article could not be loaded.</p>');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [selected]);

  // Jump to a heading once its article has actually rendered — a hash
  // present on first load (a fresh /global-manual?article=slug#heading-id
  // navigation) can't be scrolled to any earlier, since the heading doesn't
  // exist in the DOM until this article's markdown has parsed.
  useEffect(() => {
    if (loading || !html || !location.hash) return;
    const target = document.getElementById(location.hash.slice(1));
    if (target) target.scrollIntoView({ block: 'start' });
  }, [loading, html, location.hash]);

  // Groups preserve the backend's own sort order (group, then title) —
  // insertion order into a plain object is stable for string keys.
  const groupedArticles = useMemo(() => {
    const groups = {};
    allArticles.forEach((article) => {
      if (!groups[article.group]) groups[article.group] = { group: article.group, articles: [] };
      groups[article.group].articles.push(article);
    });
    return Object.values(groups);
  }, [allArticles]);

  const filteredGroups = query.trim()
    ? groupedArticles.map((group) => ({
      ...group,
      articles: group.articles.filter((article) => article.title.toLowerCase().includes(query.trim().toLowerCase())),
    })).filter((group) => group.articles.length > 0)
    : groupedArticles;

  return (
    <div className="manual-page">
      <div className="manual-nav">
        <div className="manual-search">
          <Search size={14} strokeWidth={1.75} />
          <input
            type="text"
            placeholder="Search the Manual…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {filteredGroups.map((group) => (
          <div className="manual-nav-group" key={group.group}>
            <div className="manual-nav-group-label">{group.group}</div>
            {group.articles.map((article) => (
              <button
                key={article.slug}
                type="button"
                className={`manual-nav-link ${selectedSlug === article.slug ? 'active' : ''}`}
                onClick={() => selectArticle(article.slug)}
              >
                {article.title}
              </button>
            ))}
          </div>
        ))}
        {filteredGroups.length === 0 && (
          <div className="manual-nav-empty">No articles match "{query}".</div>
        )}
      </div>
      <div className="manual-article">
        {!selected && notFoundSlug && (
          <>
            <div className="manual-crumb">Manual / <b>Article Not Available</b></div>
            <div className="manual-not-found">
              <div className="manual-not-found-title">Article Not Available</div>
              <p>Oops — this article isn't available to your current user profile.</p>
              <p>Press Back in your browser to return, or pick another article from the list on the left.</p>
            </div>
          </>
        )}
        {!selected && !notFoundSlug && (
          <div className="manual-empty">Select an article from the list.</div>
        )}
        {selected && (
          <>
            <div className="manual-crumb">{selected.group} / <b>{selected.title}</b></div>
            {loading ? (
              <div className="manual-loading">Loading…</div>
            ) : (
              // Content is authored in-repo (public/manual/*.md), not user
              // input — same trust level as any other bundled UI copy.
              <div className="manual-body" dangerouslySetInnerHTML={{ __html: html }} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ManualPage;
